import { Injectable, REQUEST_CONTEXT, inject } from '@angular/core';

import { VnRequestContext } from '@frontend/vanilla/ssr';

import { OnAppInit } from '../bootstrap/bootstrapper.service';
import { MediaQueryService } from '../browser/media-query.service';
import { WINDOW } from '../browser/window/window.token';
import { LazyAsset } from './lazy-assets.client-config';
import { ScriptsService } from './scripts.service';
import { StylesService } from './styles.service';

@Injectable()
export class LazyAssetsBootstrapService implements OnAppInit {
    private notYetLoadedInitialStyles: LazyAsset[] = [];
    private notYetLoadedInitialScripts: LazyAsset[] = [];

    private readonly styleService = inject(StylesService);
    private readonly scriptService = inject(ScriptsService);
    private readonly mediaQueryService = inject(MediaQueryService);
    readonly #window = inject(WINDOW);
    private requestContext = inject<VnRequestContext>(REQUEST_CONTEXT);

    onAppInit() {
        const styles = (this.requestContext?.vnStyles || this.#window['vn_styles'] || []) as LazyAsset[];
        const scripts = (this.requestContext?.vnScripts || this.#window['vn_scripts'] || []) as LazyAsset[];

        this.styleService.init(styles);
        this.scriptService.init(scripts);

        this.loadStyles(styles);
        this.loadScripts(scripts);

        this.mediaQueryService.observe().subscribe(() => {
            const styles = this.notYetLoadedInitialStyles;
            const scripts = this.notYetLoadedInitialScripts;
            this.notYetLoadedInitialStyles = [];
            this.notYetLoadedInitialScripts = [];

            styles.forEach((s) => this.loadInitialStyle(s));
            scripts.forEach((s) => this.loadInitialScript(s));
        });
    }

    private loadStyles(stylesheets: LazyAsset[]) {
        const importantStyles = stylesheets.filter((s) => s.lazyLoad === 1 || s.lazyLoad === 2);
        const secondaryStyles = stylesheets.filter((s) => s.lazyLoad === 3);

        const promise = Promise.all(importantStyles.map((s) => this.loadInitialStyle(s)));
        promise.then(() => secondaryStyles.forEach((s) => this.loadInitialStyle(s)));

        return promise;
    }

    private loadScripts(scripts: LazyAsset[]) {
        const importantScripts = scripts.filter((s) => s.lazyLoad === 2);
        const secondaryScripts = scripts.filter((s) => s.lazyLoad === 3);

        const promise = Promise.all(importantScripts.map((s) => this.loadInitialScript(s)));
        promise.then(() => secondaryScripts.forEach((s) => this.loadInitialScript(s)));

        return promise;
    }

    private async loadInitialStyle(style: LazyAsset): Promise<void> {
        if (!style.media || this.mediaQueryService.isActive(style.media)) {
            return this.styleService.load(style.url);
        }

        this.notYetLoadedInitialStyles.push(style);

        return Promise.resolve();
    }

    private async loadInitialScript(script: LazyAsset): Promise<void> {
        if (!script.media || this.mediaQueryService.isActive(script.media)) {
            return this.scriptService.load(script.url);
        }

        this.notYetLoadedInitialScripts.push(script);

        return Promise.resolve();
    }
}
